import {
  ConfiguratorContext_CheckoutOptionGroupFragment,
  ToggleOption_CheckoutConfigOptionGroupFragment,
  ToggleOption_CheckoutConfigPotentialOptionFragment,
  ToggleOption_ReadyPlanOptionFragment,
} from "src/fragments/circularFragments.generated";

export type RemovedOption = { displayName: string; id: string };

// all available options with extended types to check for dependencies
type FlattenedCheckoutOptionGroup = ToggleOption_ReadyPlanOptionFragment & {
  group: { groupPrerequisites: { id: string }[] };
  groupId: string;
};

export function calcSelectedOptionIds(
  allOptionGroups: ConfiguratorContext_CheckoutOptionGroupFragment[],
  currentOptionIds: string[],
  toggledGroup: ToggleOption_CheckoutConfigOptionGroupFragment,
  toggledOption: ToggleOption_CheckoutConfigPotentialOptionFragment,
): {
  newOptionIds: string[];
  removedOptions: RemovedOption[];
} {
  const isAlreadySelected = currentOptionIds.includes(toggledOption.option.id);
  let selectedOptionIds = [...currentOptionIds];

  if (toggledGroup.group.isMultiOptionGroup) {
    if (isAlreadySelected) {
      // Multi-option groups (like Spec Level) must always have one selection, so we just leave the selection as is when required
      // AKA do not allow deselection of a required option
      selectedOptionIds = toggledGroup.group.required
        ? [...currentOptionIds]
        : [...currentOptionIds.filter((id) => id !== toggledOption.option.id)];
    } else {
      const optionIdsInThisGroup = toggledGroup.options.flatMap((cco) => cco.option.id);
      selectedOptionIds = [
        // Remove all options in this group
        ...currentOptionIds.filter((id) => !optionIdsInThisGroup.includes(id)),
        // Add back the new one
        toggledOption.option.id,
      ];
    }
  } else {
    // Single-option groups (like Add Bedroom) can be toggled on/off completely
    selectedOptionIds = isAlreadySelected
      ? currentOptionIds.filter((id) => id !== toggledOption.option.id)
      : [...currentOptionIds, toggledOption.option.id];
  }

  // Get all available options with extended types to check for dependencies
  const allOptions: FlattenedCheckoutOptionGroup[] = allOptionGroups.flatMap((ccog) =>
    ccog.options.map((cco) => ({
      ...cco.option,
      group: ccog.group,
      groupId: ccog.id,
    })),
  );

  const removedOptions: ToggleOption_ReadyPlanOptionFragment[] = [];

  // Remove options with unfulfilled prerequisites and/or option conflicts
  let optionsToRemove = getOptionsToRemove(
    allOptions.filter((opt) => selectedOptionIds.includes(opt.id)),
    toggledOption,
  );
  while (optionsToRemove.nonEmpty) {
    // Process ALL options that need to be removed in this iteration
    for (const optionToRemove of optionsToRemove) {
      // Add the option to the removed options list first
      removedOptions.push(optionToRemove);
      // Remove the option from selected list
      selectedOptionIds = selectedOptionIds.filter((id) => id !== optionToRemove.id);
    }
    // Recalculate selected options and what needs to be removed
    optionsToRemove = getOptionsToRemove(
      allOptions.filter((opt) => selectedOptionIds.includes(opt.id)),
      toggledOption,
    );
  }

  // After removing invalid options, auto-select valid options for required/multi-option groups
  // e.g., Switching from Modern→Farmhouse elevation removes Gray scheme (requires Modern),
  // so auto-select Brown scheme to satisfy the required Exterior Scheme group
  for (const ccog of allOptionGroups) {
    if (ccog.group.required && !ccog.options.some((cco) => selectedOptionIds.includes(cco.option.id))) {
      // Find the first valid option in this group (one that doesn't have unfulfilled prerequisites)
      const validOption = ccog.options.find((cco) => {
        return (
          !hasUnfulfilledPrerequisites(cco.option, selectedOptionIds) &&
          !hasOptionConflicts(
            cco.option,
            allOptions.filter((opt) => selectedOptionIds.includes(opt.id)),
          )
        );
      });
      if (validOption) {
        selectedOptionIds.push(validOption.option.id);
      }
    }
  }

  return {
    newOptionIds: selectedOptionIds,
    removedOptions: removedOptions.map((ro) => ({ displayName: ro.displayName, id: ro.id })),
  };
}

/**
 * Find options to remove for two reasons:
 * 1. Prerequisites no longer satisfied: If option A requires option B, and B gets deselected,
 *    then A must also be removed (e.g., "Gray Exterior" requires "Modern Elevation")
 * 2. Conflicts with newly selected option: If the user just selected option C, and option D
 *    conflicts with C, then D should be removed to make room for the user's new choice
 *
 * We need the newlySelectedOption to determine which option should "win" the conflict, if A conflicts with B, we wouldn't
 * know whether to remove A or B.
 *
 * Based on the internal-frontend getFormRPOsToRemove function.
 */
function getOptionsToRemove(
  selectedOptions: ToggleOption_ReadyPlanOptionFragment[],
  toggledOption: ToggleOption_CheckoutConfigPotentialOptionFragment,
): ToggleOption_ReadyPlanOptionFragment[] {
  const selectedOptionIds = selectedOptions.map((so) => so.id);
  return selectedOptions.filter((rpo) => {
    return (
      // Don't bother removing personalization options b/c it creates noise of "this sink1/faucet2 are going away",
      // particularly b/c the names are long/weird; and the BE will remove them for us anyway.
      !rpo.optionGroup.personalizationType &&
      (hasUnfulfilledPrerequisites(rpo, selectedOptionIds) ||
        hasUnfulfilledOptionGroupPrerequisites(rpo, selectedOptions) ||
        // If currentOption was just selected, we will ignore its conflicts, and instead remove whatever
        // other options it conflicts with, i.e. we prefer to keep the newly selected option.
        (rpo.id !== toggledOption.option.id && hasOptionConflicts(rpo, selectedOptions)))
    );
  });
}

/**
 * Check if an option conflicts with a newly selected option.
 * Returns true if the current option conflicts with the newly selected one
 */
function hasOptionConflicts(
  rpo: ToggleOption_ReadyPlanOptionFragment,
  selectedOptions: ToggleOption_ReadyPlanOptionFragment[],
): boolean {
  return (
    // Do I conflict with any of `selectedOptions`?
    rpo.optionConflicts.some((conflict) => selectedOptions.some((other) => conflict.id === other.id)) ||
    // Does anyone in `selectedOptions` conflict with me?
    selectedOptions.some((other) => other.optionConflicts.some((conflict) => conflict.id === rpo.id))
  );
}

/**
 * Check if an option has unfulfilled prerequisites.
 * Returns true if the option's prerequisites are NOT satisfied.
 */
function hasUnfulfilledPrerequisites(rpo: ToggleOption_ReadyPlanOptionFragment, newOptionIds: string[]): boolean {
  if (rpo.optionPrerequisites.isEmpty) return false;
  // We might have prereqs=[sl:essential,sl:premium,addbedroom], which means "either
  // essential or premium is required as a prereq", so we need to group our prereqs by rpog and
  // require at least 1 rpo from each group
  const prereqsByGroup = rpo.optionPrerequisites.groupBy((prereq) => prereq.optionGroup.id);
  return !prereqsByGroup.toValues().every((prereqsInGroup) => {
    // This will be `prereqsInGroup=[sl:essential,sl:premium]` and we just need one of them enabled
    return prereqsInGroup.some((prereq) => newOptionIds.includes(prereq.id));
  });
}

function hasUnfulfilledOptionGroupPrerequisites(
  rpo: ToggleOption_ReadyPlanOptionFragment,
  selectedOptions: ToggleOption_ReadyPlanOptionFragment[],
): boolean {
  if (rpo.optionGroup.groupPrerequisites.isEmpty) return false;
  const selectedOptionGroups = selectedOptions.map((so) => so.optionGroup.id);
  return !rpo.optionGroup.groupPrerequisites.some((og: { id: string }) => selectedOptionGroups.includes(og.id));
}
